import { ReactNode } from 'react';
import { Checkbox as AriaCheckbox } from 'react-aria-components';
import { Check } from 'lucide-react';
import { cx } from './cx';
import { Icon } from './Icon';
import { typography } from './Text';

export interface CheckboxProps {
  isSelected: boolean;
  onChange: (isSelected: boolean) => void;
  label: ReactNode;
  isDisabled?: boolean;
  /** `chip` reads as a selectable filter pill; `inline` is a plain checkbox. */
  variant?: 'chip' | 'inline';
  className?: string;
}

/**
 * The Regask checkbox — react-aria `Checkbox`. `chip` is a bordered selectable
 * (used for list filters like "Needs my approval") that tints brand when on;
 * `inline` is the plain Untitled UI checkbox + label.
 */
export const Checkbox = ({
  isSelected,
  onChange,
  label,
  isDisabled,
  variant = 'chip',
  className,
}: CheckboxProps) => (
  <AriaCheckbox
    isSelected={isSelected}
    isDisabled={isDisabled}
    onChange={onChange}
    className={cx(
      typography.label,
      'group inline-flex cursor-pointer items-center gap-2 outline-none transition',
      'data-[focus-visible]:ring-4 data-[focus-visible]:ring-[rgb(var(--c-ring)/0.24)] disabled:opacity-60',
      variant === 'chip' &&
        'h-10 rounded-lg border border-strong bg-surface px-3 text-fg-secondary shadow-xs hover:bg-muted-strong selected:border-brand-solid/50 selected:bg-brand-solid/12 selected:text-brand-text',
      variant === 'inline' && 'text-fg-secondary',
      className,
    )}
  >
    <span
      className={cx(
        'flex h-5 w-5 shrink-0 items-center justify-center rounded-md border border-strong bg-surface text-base-white transition',
        'group-selected:border-brand-solid group-selected:bg-brand-solid',
      )}
    >
      <Icon
        as={Check}
        size="xs"
        strokeWidth={3}
        className="opacity-0 group-selected:opacity-100"
      />
    </span>
    {label}
  </AriaCheckbox>
);
